import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    CircularProgress,
    makeStyles,
    Tooltip,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import NumberUtils from 'lib/NumberUtils';
import { Scanner } from '@yudiel/react-qr-scanner';
import FilterCenterFocusIcon from '@material-ui/icons/FilterCenterFocus';
import useVinValidation from 'components/modules/inventory/hooks/useVinValidation';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';

const useStyles = makeStyles((theme) => ({
    scannerParent: {
        '@media (orientation: landscape)': {
            position: 'fixed',
            width: '100%',
            top: 0,
            left: 0,
            zIndex: 9999,
        },
        '@media (orientation: portrait)': {
            position: 'fixed',
            left: '10px',
            top: '10px',
            height: '100%',
            width: '95%',
            marginRight: '10px',
            zIndex: 9999,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.outerSpace,
        },
    },
    scannerParentDesktop: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '700px',
        zIndex: 9999,
    },
    shortButton: {
        minWidth: 'auto !important',
        paddingLeft: '5px',
        paddingRight: '5px',
    },
    closeButton: {
        opacity: 0,
        animation: 'fadeIn 0.5s ease-in forwards',
        animationDelay: '1s',
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 99999,
        cursor: 'pointer',
        backgroundColor: theme.palette.background.white,
        '& > svg': {
            fill: theme.palette.background.red,
            width: '34px',
            height: '34px',
        },
    },
}));

const VinScanner = ({
    loading,
    onChange,
    decodeVin,
    vinNumber,
    scanStock,
    shortButton,
    onSearchVehicle,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const classes = useStyles();
    const [showScanner, setShowScanner] = useState(false);
    const contentScanButton = (loading) ? <CircularProgress color="inherit" size={20} /> : <FilterCenterFocusIcon />;

    const {
        isInvalidVINLength,
        isInvalidVINChars,
    } = useVinValidation('none', vinNumber);

    const generalErrorMessage = 'Error scanning code';
    useEffect(() => {
        if (
            !StringUtils.isEmpty(vinNumber)
            && showScanner
        ) {
            if (!isInvalidVINLength && !isInvalidVINChars) {
                decodeVin();
                setShowScanner(false);
            } else {
                ModalUtils.errorMessage(null, generalErrorMessage);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vinNumber, isInvalidVINLength, isInvalidVINChars]);

    const checkCamaraAvailability = async () => {
        const errorMessage = 'You do not have a camera on this device';

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoInputs = devices.filter((device) => device.kind === 'videoinput');
            if (videoInputs.length === 0) {
                ModalUtils.errorMessage(null, errorMessage);
                return;
            }

            setShowScanner(true);
        } catch (error) {
            ModalUtils.errorMessage(null, errorMessage);
        }
    };

    const extractVIN = (vin) => {
        const split = vin.split(',');
        return split.find((s) => (s ?? '').trim().length === 17) ?? '';
    };

    const onReadCode = (input) => {
        if (input?.length === 0) {
            ModalUtils.errorMessage(null, generalErrorMessage);
            setShowScanner(false);
        }

        const code = input[0].rawValue ?? '';
        if (scanStock && NumberUtils.isOnlyNumber(code)) {
            onSearchVehicle(code);
            setShowScanner(false);

            return;
        }

        const vin = extractVIN(code);
        if (StringUtils.isEmpty(vin)) {
            ModalUtils.errorMessage(null, generalErrorMessage);
            setShowScanner(false);
        }

        if (scanStock) {
            onSearchVehicle(vin);
            setShowScanner(false);

            return;
        }

        onChange('vinNumber', vin);
    };

    return (
        <>
            <Tooltip title={scanStock ? 'Stock / VIN Scanner' : 'VIN Scanner'} placement="top">
                <Button
                    className={shortButton ? classes.shortButton : ''}
                    variant="outlined"
                    disabled={loading}
                    onClick={() => checkCamaraAvailability()}
                >
                    { contentScanButton }
                </Button>
            </Tooltip>
            {showScanner && (
                <div className={isMobile ? classes.scannerParent : classes.scannerParentDesktop}>
                    <div
                        onClick={() => setShowScanner(false)}
                        className={classes.closeButton}
                    >
                        <HighlightOffOutlinedIcon />
                    </div>
                    <Scanner
                        onScan={(detectedCodes) => onReadCode(detectedCodes)}
                        onError={(error) => ModalUtils.errorMessage(null, error.message)}
                        components={{
                            audio: true,
                            onOff: false,
                            torch: false,
                            zoom: false,
                            finder: false,
                        }}
                        scanDelay={1000}
                        constraints={{
                            facingMode: 'environment',
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                        }}
                        formats={[
                            'aztec',
                            'code_128',
                            'code_39',
                            'code_93',
                            'codabar',
                            'databar',
                            'databar_expanded',
                            'data_matrix',
                            'dx_film_edge',
                            'ean_13',
                            'ean_8',
                            'itf',
                            'maxi_code',
                            'micro_qr_code',
                            'pdf417',
                            'qr_code',
                            'rm_qr_code',
                            'upc_a',
                            'upc_e',
                            'linear_codes',
                            'matrix_codes',
                        ]}
                    />
                </div>
            )}
        </>
    );
};

VinScanner.propTypes = {
    loading: PropTypes.bool,
    vinNumber: PropTypes.string,
    onChange: PropTypes.func,
    decodeVin: PropTypes.func,
    scanStock: PropTypes.bool,
    shortButton: PropTypes.bool,
    onSearchVehicle: PropTypes.func,
};

VinScanner.defaultProps = {
    loading: false,
    scanStock: false,
    shortButton: false,
    vinNumber: '',
    onChange: () => null,
    decodeVin: () => null,
    onSearchVehicle: () => null,
};

export default VinScanner;
